import { Global, withEmotionCache } from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { PRERENDER_RENDERING } from 'my-core/analytics/analytics-constants';
import createTheme from 'my-theme/myTheme';

import MainLayout from './MainLayout';
import { useSiteWideBanner } from './SiteWideBanner';

import CssBaseline from '@mui/material/CssBaseline';

export default withEmotionCache(function MainStyles(_, cache) {
  // Override @emotion sheet `speedy` option to make sure prerendered pages include css rules
  // in DOM content (bypass insertRule API). Needs to be in component tree child of <StyleEngineProvider />
  if (PRERENDER_RENDERING) cache.sheet.isSpeedy = false;

  const isBannerOpen = !!useSiteWideBanner()?.isOpen;

  // Stays in `dark_mode` so after the Halloween revert a stored 'halloween' still reads as dark.
  const [themeMode, setThemeMode] = useState(() => {
    const ls = window.localStorage?.getItem('dark_mode');
    if (ls === 'halloween') return 'halloween';
    return ls && ls !== 'false' ? 'dark' : 'light';
    // return !!(ls || window.matchMedia?.('(prefers-color-scheme: dark)').matches);
  });
  const [pendingThemeMode, setPendingThemeMode] = useState(themeMode);

  const [darkModeTransitioning, setDarkModeTransitioning] = useState(false);
  useEffect(() => {
    setThemeMode(pendingThemeMode);
    const to = setTimeout(() => {
      setDarkModeTransitioning(false);
    }, 1000);
    return () => clearTimeout(to);
  }, [darkModeTransitioning, pendingThemeMode]);

  const updateThemeMode = useCallback(themeMode => {
    setDarkModeTransitioning(true);
    setPendingThemeMode(themeMode);
    window.localStorage?.setItem('dark_mode', themeMode === 'halloween' ? themeMode : themeMode === 'dark');
  }, []);

  const theme = useMemo(() => {
    const t = createTheme(themeMode);
    if (!isBannerOpen) return t;

    const APPBAR_OFFSET = t.constants.APPBAR_HEIGHT * 2;
    const APPBAR_OFFSET_UNITS = APPBAR_OFFSET / 8;

    return {
      ...t,
      constants: {
        ...t.constants,
        APPBAR_OFFSET,
        APPBAR_OFFSET_UNITS,
      },
    };
  }, [themeMode, isBannerOpen]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {darkModeTransitioning && (
        <Global
          styles={{
            '*, *:before': { transition: 'background-color 0.5s, border-color 0.5s !important' },
            'svg path, svg circle, a, button, p, span, input, .MuiPickersInputBase-sectionsContainer, h1, h2, h3, h4, h5, h6, td':
              {
                transition: 'background-color 0.5s, color 0.5s, fill 0.5s, stroke 0.5s, border-color 0.5s !important',
              },
          }}
        />
      )}
      <MainLayout onChangeThemeMode={updateThemeMode} themeMode={pendingThemeMode} />
    </ThemeProvider>
  );
});
